"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/casting/Vacuum-Casting/Materials-Finish-Colors";

type MaterialItem = {
	keyPrefix: string;
	image: string;
	bulletCount: number;
};

const SILICONE_MATERIALS: readonly MaterialItem[] = [
	{
		keyPrefix: "materialsFinishColorsSilicone1",
		image: `${IMAGE_BASE}/Cast Silicone Materials/shore-a-10-60-silicone.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsSilicone2",
		image: `${IMAGE_BASE}/Cast Silicone Materials/rtv-2-tin-cure-silicone.webp`,
		bulletCount: 0,
	},
	{
		keyPrefix: "materialsFinishColorsSilicone3",
		image: `${IMAGE_BASE}/Cast Silicone Materials/rtv-2-platinum-cure-silicone.webp`,
		bulletCount: 0,
	},
] as const;

const URETHANE_MATERIALS: readonly MaterialItem[] = [
	{
		keyPrefix: "materialsFinishColorsUrethane1",
		image: `${IMAGE_BASE}/Cast Urethane Materials/soft-rubber.webp`,
		bulletCount: 4,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane2",
		image: `${IMAGE_BASE}/Cast Urethane Materials/abs-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane3",
		image: `${IMAGE_BASE}/Cast Urethane Materials/pmma-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane4",
		image: `${IMAGE_BASE}/Cast Urethane Materials/pc-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane5",
		image: `${IMAGE_BASE}/Cast Urethane Materials/nylon-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane6",
		image: `${IMAGE_BASE}/Cast Urethane Materials/pp-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane7",
		image: `${IMAGE_BASE}/Cast Urethane Materials/pom-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane8",
		image: `${IMAGE_BASE}/Cast Urethane Materials/pe-like.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsUrethane9",
		image: `${IMAGE_BASE}/Cast Urethane Materials/abs-pc-like.webp`,
		bulletCount: 3,
	},
] as const;

const COLORS = [
	{
		keyPrefix: "materialsFinishColorsColor1",
		image: `${IMAGE_BASE}/Color/standard-black.webp`,
	},
	{
		keyPrefix: "materialsFinishColorsColor2",
		image: `${IMAGE_BASE}/Color/pure-white.webp`,
	},
	{
		keyPrefix: "materialsFinishColorsColor3",
		image: `${IMAGE_BASE}/Color/transparent.webp`,
	},
	{
		keyPrefix: "materialsFinishColorsColor4",
		image: `${IMAGE_BASE}/Color/custom-colors.webp`,
	},
] as const;

const FINISHES = [
	{
		keyPrefix: "materialsFinishColorsFinish1",
		image: `${IMAGE_BASE}/Surface Finish/spi-finish.webp`,
		bulletCount: 3,
	},
	{
		keyPrefix: "materialsFinishColorsFinish2",
		image: `${IMAGE_BASE}/Surface Finish/vdi-finish.webp`,
		bulletCount: 3,
	},
] as const;

function ExpandIcon({ size = 16, className = "" }: { size?: number; className?: string }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
		>
			<path
				d="M15.5938 1.125L10.6875 6H14H14.5V7H14H9.5H9V6.5V2V1.5H10V2V5.3125L14.875 0.40625L15.25 0.0625L15.9375 0.75L15.5938 1.125ZM0.375 14.9062L5.28125 10H2H1.5V9H2H6.5H7V9.5V14V14.5H6V14V10.7188L1.09375 15.625L0.75 15.9688L0.03125 15.25L0.375 14.9062Z"
				fill="currentColor"
			/>
		</svg>
	);
}

type TFn = ReturnType<typeof useTranslations<"new-vacuum-casting">>;

type TabKey = "materials" | "colors" | "finishes";

export default function VacuumCastingMaterialsFinishColors() {
	const t = useTranslations("new-vacuum-casting");
	const [activeTab, setActiveTab] = useState<TabKey>("materials");
	const [activeSilicone, setActiveSilicone] = useState(0);
	const [activeUrethane, setActiveUrethane] = useState(0);

	const tabs: { key: TabKey; labelKey: string }[] = [
		{ key: "materials", labelKey: "materialsFinishColorsTabMaterials" },
		{ key: "colors", labelKey: "materialsFinishColorsTabColors" },
		{ key: "finishes", labelKey: "materialsFinishColorsTabFinishes" },
	];

	const descKey =
		activeTab === "materials"
			? "materialsFinishColorsMaterialsDesc"
			: activeTab === "colors"
				? "materialsFinishColorsColorsDesc"
				: "materialsFinishColorsFinishesDesc";

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("materialsFinishColorsTitle")}
				</h2>

				<div className="mb-3 flex flex-wrap justify-center gap-3 sm:mb-4">
					{tabs.map((tab) => (
						<button
							key={tab.key}
							type="button"
							onClick={() => setActiveTab(tab.key)}
							className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors sm:px-6 sm:text-base ${
								activeTab === tab.key
									? "border-themePrimary400 bg-themePrimary400 text-white"
									: "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
							}`}
						>
							{t(tab.labelKey as any)}
						</button>
					))}
				</div>

				<p className="mx-auto mb-6 max-w-4xl text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t(descKey as any)}
				</p>

				{activeTab === "materials" && (
					<div className="space-y-10 sm:space-y-12 lg:space-y-16">
						<MaterialGroup
							t={t}
							titleKey="materialsFinishColorsSiliconeGroupTitle"
							items={SILICONE_MATERIALS}
							activeIndex={activeSilicone}
							onSelect={setActiveSilicone}
						/>
						<MaterialGroup
							t={t}
							titleKey="materialsFinishColorsUrethaneGroupTitle"
							items={URETHANE_MATERIALS}
							activeIndex={activeUrethane}
							onSelect={setActiveUrethane}
						/>
						<p className="text-sm leading-relaxed text-gray-700 md:text-base">
							<span className="font-semibold text-black">{t("materialsFinishColorsNoteLabel")}</span>{" "}
							{t("materialsFinishColorsNoteText")}
						</p>
					</div>
				)}

				{activeTab === "colors" && <ColorsPanel t={t} />}

				{activeTab === "finishes" && <FinishesPanel t={t} />}
			</div>
		</section>
	);
}

function MaterialGroup({
	t,
	titleKey,
	items,
	activeIndex,
	onSelect,
}: {
	t: TFn;
	titleKey: string;
	items: readonly MaterialItem[];
	activeIndex: number;
	onSelect: (i: number) => void;
}) {
	const item = items[activeIndex] ?? items[0]!;

	return (
		<div>
			<h3 className="mb-4 text-lg font-bold text-black sm:mb-6 sm:text-xl md:text-2xl">
				{t(titleKey as any)}
			</h3>
			<div className="mb-6 border-t-2 border-themePrimary400" />

			<div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_1fr] lg:gap-x-6">
				<div className="flex flex-row gap-0 overflow-x-auto border-b border-gray-200 lg:flex-col lg:overflow-x-visible lg:border-b-0">
					{items.map((it, i) => (
						<button
							key={it.keyPrefix}
							type="button"
							onClick={() => onSelect(i)}
							className={`whitespace-nowrap px-4 py-3 text-left text-sm font-medium transition-colors sm:py-4 sm:text-base ${
								activeIndex === i
									? "bg-themePrimary400 font-bold text-white"
									: "bg-[#f6f6f6] text-gray-700 hover:bg-gray-50"
							}`}
						>
							{t(`${it.keyPrefix}Name` as any)}
						</button>
					))}
				</div>

				<div className="flex items-start justify-center px-6 sm:px-8 md:px-10">
					<div className="relative aspect-[4/3] w-full max-w-md bg-[#e9e9e9]">
						<Image
							src={item.image}
							alt={t(`${item.keyPrefix}ImageAlt` as any)}
							fill
							className="object-contain"
							sizes="(max-width: 1024px) 100vw, 33vw"
						/>
					</div>
				</div>

				<div className="flex flex-col justify-start px-6 sm:px-8 md:px-10">
					<h4 className="mb-3 text-lg font-bold text-black sm:mb-4 sm:text-xl md:text-2xl">
						{t(`${item.keyPrefix}Name` as any)}
					</h4>
					<p className="mb-4 text-sm leading-relaxed text-black md:text-base">
						{t(`${item.keyPrefix}Desc` as any)}
					</p>
					{item.bulletCount > 0 && (
						<ul className="space-y-3">
							{Array.from({ length: item.bulletCount }, (_, bi) => {
								const title = t(`${item.keyPrefix}Bullet${bi + 1}Title` as any);
								const desc = t(`${item.keyPrefix}Bullet${bi + 1}Desc` as any);
								return (
									<li
										key={bi}
										className="flex items-start gap-2 text-sm leading-relaxed text-gray-800 md:text-base"
									>
										<svg
											className="mt-0.5 h-5 w-5 shrink-0 text-themePrimary400"
											viewBox="0 0 20 20"
											fill="currentColor"
											aria-hidden
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										<span>
											{title ? (
												<>
													<span className="font-semibold text-black">{title}</span>{" "}
												</>
											) : null}
											{desc}
										</span>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
}

function ColorsPanel({ t }: { t: TFn }) {
	return (
		<div>
			<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:gap-8">
				{COLORS.map((card) => (
					<article
						key={card.keyPrefix}
						className="overflow-hidden rounded-lg border border-gray-200 bg-[#eeeeee]"
					>
						<div className="relative aspect-[16/10] w-full bg-white">
							<Image
								src={card.image}
								alt={t(`${card.keyPrefix}ImageAlt` as any)}
								fill
								className="object-contain p-6 sm:p-8"
								sizes="(max-width: 640px) 100vw, 50vw"
							/>
						</div>
						<div className="p-5 sm:p-6 md:p-7">
							<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">
								{t(`${card.keyPrefix}Title` as any)}
							</h3>
							<p className="text-sm leading-relaxed text-black md:text-base">
								{t(`${card.keyPrefix}Desc` as any)}
							</p>
						</div>
					</article>
				))}
			</div>
			<p className="mt-6 text-sm leading-relaxed text-gray-700 md:text-base">
				{t("materialsFinishColorsColorNote")}
			</p>
		</div>
	);
}

type FinishItem = (typeof FINISHES)[number];

function FinishCard({ card, t }: { card: FinishItem; t: TFn }) {
	const [isHovered, setIsHovered] = useState(false);

	return (
		<div
			className="group relative h-[400px] w-full overflow-hidden sm:h-[450px]"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Image
				src={card.image}
				alt={t(`${card.keyPrefix}ImageAlt` as any)}
				fill
				className="object-cover transition-transform duration-500 group-hover:scale-105"
				sizes="(max-width: 1024px) 100vw, 50vw"
			/>

			<div
				className={`absolute bottom-0 left-0 right-0 mx-auto w-[95%] bg-[#2a2a2a] px-4 py-3 transition-all duration-500 sm:px-5 sm:py-4 ${
					isHovered ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
				}`}
			>
				<div className="flex items-center justify-between">
					<h3 className="text-white sm:text-base">{t(`${card.keyPrefix}Title` as any)}</h3>
					<ExpandIcon size={18} className="text-white" />
				</div>
			</div>

			<div
				className={`absolute bottom-0 left-0 right-0 mx-auto max-h-[88%] w-[95%] overflow-y-auto bg-[#1a1a1a]/95 p-5 transition-all duration-500 sm:p-6 ${
					isHovered ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
				}`}
			>
				<div className="mb-4 flex items-center justify-between border-b border-[#666667] pb-3 sm:mb-5 sm:pb-4">
					<h3 className="text-base text-white sm:text-base">{t(`${card.keyPrefix}Title` as any)}</h3>
					<ExpandIcon size={20} className="text-white" />
				</div>

				<p className="mb-4 text-sm leading-relaxed text-white sm:text-sm">
					{t(`${card.keyPrefix}Desc` as any)}
				</p>

				<div className="space-y-3 sm:space-y-4">
					{Array.from({ length: card.bulletCount }, (_, bi) => (
						<div
							key={bi}
							className="flex items-start gap-2 border-b border-[#666667] pb-2 sm:pb-3 [&:last-child]:border-b-0"
						>
							<svg
								className="mt-0.5 h-5 w-5 shrink-0 text-themePrimary400"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden
							>
								<path
									fillRule="evenodd"
									d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="text-sm text-white sm:text-sm">
								{t(`${card.keyPrefix}Bullet${bi + 1}` as any)}
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function FinishesPanel({ t }: { t: TFn }) {
	return (
		<div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-8">
			{FINISHES.map((card) => (
				<FinishCard key={card.keyPrefix} card={card} t={t} />
			))}
		</div>
	);
}
